import { Logo } from "./Logo";

const Check = () => (
  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
  </svg>
);

const SAMPLE_SCORES = [
  ['Pagos', 1.5],
  ['Cuidado del inmueble', 3],
  ['Cumplimiento del contrato', 2],
];

// Illustrative report so visitors see what they get; clearly labeled as an example
const SampleReport = () => (
  <div className="relative bg-white rounded-2xl shadow-2xl shadow-amber-950/30 p-5 w-full max-w-sm text-left rotate-[-1.5deg]">
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 font-bold flex items-center justify-center flex-shrink-0">JM</div>
        <div className="min-w-0">
          <p className="font-bold text-amber-950 truncate">Inquilino de ejemplo</p>
          <p className="text-xs text-amber-700/80">3 evaluaciones &middot; 2 de propietarios verificados</p>
        </div>
      </div>
      <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0">
        <span className="w-2 h-2 rounded-full bg-red-500" aria-hidden="true" />Riesgo alto
      </span>
    </div>
    <div className="space-y-3">
      {SAMPLE_SCORES.map(([label, score]) => (
        <div key={label}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-amber-900 font-medium">{label}</span>
            <span className="text-amber-700/80">{score}/5</span>
          </div>
          <div className="h-2 rounded-full bg-amber-100 overflow-hidden">
            <div className={`h-full rounded-full ${score < 2.5 ? 'bg-red-500' : 'bg-amber-500'}`} style={{ width: `${score * 20}%` }} />
          </div>
        </div>
      ))}
    </div>
    <p className="mt-4 text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 italic">
      &ldquo;Dej&oacute; de pagar a partir del cuarto mes y sali&oacute; sin avisar.&rdquo;
    </p>
    <span className="absolute -top-3 -right-3 bg-amber-950 text-amber-100 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow">Ejemplo</span>
  </div>
);

// Soft light shapes and a faint grid for depth on amber gradient panels
const BrandBackdrop = () => (
  <>
    <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-amber-300/40 blur-3xl" aria-hidden="true" />
    <div className="pointer-events-none absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-amber-950/30 blur-3xl" aria-hidden="true" />
    <div className="pointer-events-none absolute inset-0 opacity-[0.07] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:36px_36px]" aria-hidden="true" />
  </>
);

// The tagline with "morosos" underlined
const Tagline = ({ className = "" }) => (
  <h2 className={`font-extrabold leading-[1.08] tracking-tight ${className}`}>
    &ldquo;El bur&oacute; de cr&eacute;dito para inquilinos{' '}
    <span className="relative whitespace-nowrap">
      <span className="relative z-10">morosos</span>
      <span className="absolute left-0 right-0 bottom-1 h-3 bg-amber-950/35 rounded" aria-hidden="true" />
    </span>&rdquo;
  </h2>
);

// Split screen for login and sign-up: brand story on the left, the form on the right
const AuthLayout = ({ title, subtitle, onHome, children }) => (
  <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] text-left">
    <aside className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-amber-800 text-white px-6 py-8 sm:px-10 lg:px-14 lg:py-12 flex flex-col">
      <BrandBackdrop />

      <div className="relative"><Logo light onClick={onHome} /></div>

      <div className="relative flex-1 flex flex-col justify-center mt-8 lg:mt-0">
        <p className="text-amber-100 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-3">Historial de inquilinos</p>
        <Tagline className="text-3xl sm:text-4xl xl:text-5xl max-w-xl" />
        <p className="mt-4 text-amber-50/95 text-base sm:text-lg max-w-md leading-relaxed">
          Antes de entregar las llaves, descubre c&oacute;mo pag&oacute;, c&oacute;mo cuid&oacute; el inmueble y si cumpli&oacute; su contrato con otros propietarios.
        </p>

        <div className="hidden lg:flex mt-10 justify-start xl:pl-6">
          <SampleReport />
        </div>
      </div>

      <ul className="relative mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-amber-50">
        {['Búsqueda gratis', 'Propietarios verificados', 'Datos protegidos'].map(t => (
          <li key={t} className="flex items-center gap-1.5"><Check />{t}</li>
        ))}
      </ul>
    </aside>

    <main className="bg-amber-50/60 flex items-center justify-center px-4 py-10 sm:px-8">
      <div className="w-full max-w-md">
        <div className="mb-7">
          <h1 className="text-3xl font-extrabold tracking-tight text-amber-950">{title}</h1>
          {subtitle && <p className="text-amber-800/80 mt-1.5">{subtitle}</p>}
        </div>
        <div className="bg-white rounded-2xl shadow-xl shadow-amber-200/50 border border-amber-100 p-6 sm:p-8">
          {children}
        </div>
      </div>
    </main>
  </div>
);

export { AuthLayout, BrandBackdrop, SampleReport, Tagline };
